import React, { useState, useEffect, createRef } from 'react';
import {
	TextPassage,
	BulletedList,
	TextLink,
	Table,
	Toolbar,
	Button,
	Select,
	TextInput,
	PlusFilledIcon,
	ProgressBar,
	Alert,
	AlertVariant,
} from 'react-unity';
import SectionHomeWrapper from '../common/wrappers/SectionHomeWrapper';
import '../common/wrappers/SectionHomeWrapper.css';
import CloudAccountsService from '../../services/CloudAccountsService';
import CloudAccount from '../../models/CloudAccount';
import { authenticatedUser } from '../../authentication/authModule';
import { UserRole } from '../../models/enums/UserRole';
import './CloudAccountsHome.css';
import CloudAccountRequestModal from './components/CloudAccountRequestModal';
import CloudAccountDeletionModal from './components/CloudAccountDeletionModal';
import ConfirmModal from '../common/modals/ConfirmModal';
import { WorkflowState } from '../../models/enums/WorkflowState';
import Tooltip from '../common/Tooltip';
import Paginator from '../common/tables/Paginator';
import CloudAccountsHomeTableRow from './CloudAccountsHomeTableRow';
import CloudAccountReasonModal from './components/CloudAccountReasonModal';

const AlreadyHasCloudAccountsMessage = 'Our records show you already have an active account for each of the supported account types listed above.'
const NoAvailableCloudAccountTypesMessage = 'Cloud accounts can only be requested with @exxonmobil.com.cn accounts.'

const CloudAccountsHome = () => {

	const cloudAccountsService = new CloudAccountsService();
	const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout>(undefined);

	const [cloudAccounts, setCloudAccounts] = useState<CloudAccount[]>([]);
	const [filteredAccounts, setFilteredAccounts] = useState<CloudAccount[]>([]);
	const [accountsOnPage, setAccountsOnPage] = useState<CloudAccount[]>([]);
	const [selectedCloudAccount, setSelectedCloudAccount] = useState<CloudAccount>(undefined);
	const [searchText, setSearchText] = useState<string>('');
	const [searchDropdownStatus, setSearchDropdownStatus] = useState<string>('all');
	const [requestAccountModalVisible, setRequestAccountModalVisible] = useState<boolean>(false);
	const [deleteAccountModalVisible, setDeleteAccountModalVisible] = useState<boolean>(false);
	const [resetPasswordModalVisible, setResetPasswordModalVisible] = useState<boolean>(false);
	const [resetMfaModalVisible, setResetMfaModalVisible] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [alertVisible, setAlertVisible] = useState<boolean>(false);
	const [alertText, setAlertText] = useState('');
	const [alertType, setAlertType] = useState<AlertVariant>('default');
	const [approveModalVisible, setApproveModalVisible] = useState<boolean>(false);
	const [rejectModalVisible, setRejectModalVisible] = useState<boolean>(false);
	const [reasonModalVisible, setReasonModalVisible] = useState<boolean>(false);
	const [rejectionReason, setRejectionReason] = useState<string>('');
	const [availableAccountTypes, setAvailableAccountTypes] = useState<string[]>([]);
	const [actionsEnabled, setActionsEnabled] = useState<boolean>(true);

	useEffect(() => {
		fetchData()
	}, []);

	const fetchData = async () => {
		setSearchDropdownStatus(
			authenticatedUser.isInRole(UserRole.CloudAccountAdmin) ? 'Submitted' : 'All'
		);
		try {
			await updateState();
		}
		catch (error) {
			const HTTP_INTERNAL_SERVER_ERROR = 500;
			if (error.status === HTTP_INTERNAL_SERVER_ERROR) {
				handleError(error, "Error gathering cloud accounts data");
			}
		}
		setIsLoading(false);
	}


	const updateState = async () => {
		let myCloudAccounts = await cloudAccountsService.getMyAccounts();

		await Promise.all(
			myCloudAccounts.map(async (cloudAccount) => {
				cloudAccount.canResetMFA = true;
			})
		);

		const myCloudAccountsForApprove = await cloudAccountsService.getAccountsForApprove();
		let cloudOnlyAccounts = [...myCloudAccounts, ...myCloudAccountsForApprove];

		if (authenticatedUser.isInRole(UserRole.CloudAccountAdmin)) {
			cloudOnlyAccounts = await cloudAccountsService.getAll();

			await Promise.all(
				cloudOnlyAccounts.map(async (cloudOnlyAccount) => {
					myCloudAccounts.forEach(Account => {
						if (Account.id == cloudOnlyAccount.id)
							cloudOnlyAccount.canResetMFA = true;
						else;
					});
				})
			);
		}

		cloudOnlyAccounts.sort((a, b) =>
			new Date(b.workflowInstance.createdDate).getTime() - new Date(a.workflowInstance.createdDate).getTime());

		const supportedDomains = (await cloudAccountsService.getSupportedDomains()).map((type) => type.domainName);
		const myExistingDomains = myCloudAccounts.filter((account) =>
			account.workflowInstance.currentWorkflowInstanceState.workflowState.name === WorkflowState.Approved.name ||
			account.workflowInstance.currentWorkflowInstanceState.workflowState.name === WorkflowState.Submitted.name)
			.map((account) => account.createdUserPrincipalName.split('@')[1]);
		const availableAccountTypes = supportedDomains.filter((d) => !myExistingDomains.includes(d))

		setCloudAccounts(cloudOnlyAccounts);
		setFilteredAccounts(cloudOnlyAccounts);
		setAvailableAccountTypes(availableAccountTypes);
	};

	const handleStatusDropdownChange = (e) => {
		const dropdownValue = e.target?.value ?? e;
		const searchTxt = searchText.toLowerCase();

		applyFilters(searchTxt, dropdownValue);
	};

	const handleSearchTextChange = (e) => {
		const searchText = e.target?.value.trim() ?? e;
		const dropdownValue = searchDropdownStatus;

		applyFilters(searchText, dropdownValue);
	};

	const applyFilters = (searchText: string, dropdownValue: string) => {
		
		let filteredAccounts = cloudAccounts.filter(account =>
			account.createdUserPrincipalName.toLowerCase().includes(searchText.toLowerCase()));

		if (dropdownValue !== 'All') {
			filteredAccounts = filteredAccounts.filter(account =>
				account.workflowInstance.currentWorkflowInstanceState.workflowState.name.toLowerCase() ===
				dropdownValue.toLowerCase());
		}

		setFilteredAccounts(filteredAccounts);
		setSearchText(searchText);
		setSearchDropdownStatus(dropdownValue);
	};

	const handleSubmitAlert = (alertText: string) => {
		setRequestAccountModalVisible(false);
		setDeleteAccountModalVisible(false);
		setAlertVisible(true);
		setAlertType('default');
		setAlertText(alertText)
		setActionsEnabled(true);
	};

	const handleSuccessAlert = (alertText: string) => {
		setRequestAccountModalVisible(false);
		setDeleteAccountModalVisible(false);
		setAlertVisible(true);
		setAlertType('success');
		setAlertText(alertText);
		setActionsEnabled(true);

		handleSetTimeout();
	};

	const handleAccountChange = (oldAccount: CloudAccount, newAccount: CloudAccount) => {
		const index = cloudAccounts.indexOf(oldAccount);
		const accounts = cloudAccounts;
		accounts[index] = newAccount;

		// update state and filter again to see changes reflected
		setCloudAccounts(accounts);
		applyFilters(searchText, searchDropdownStatus)
	};

	const handlePasswordResetRequest = async () => {
		setResetPasswordModalVisible(false)
		try {
			const cloudAccount = selectedCloudAccount;

			handleSubmitAlert('Submitting request...');

			await cloudAccountsService.requestPasswordReset(cloudAccount.createdUserPrincipalName);

			handleSuccessAlert(`An email has been sent containing a link to update the password for your account. 
								Please check your junk folder if you don't receive anything within a few minutes.`);
		} catch (error) {
			handleError(error);
		}
	};

	const handleMfaResetRequest = async () => {
		setResetMfaModalVisible(false);
		try {
			const cloudAccount = selectedCloudAccount;

			handleSubmitAlert('Submitting request...');

			await cloudAccountsService.resetMfaConfiguration(cloudAccount.createdUserPrincipalName, authenticatedUser.userEmail);

			handleSuccessAlert(`The MFA configuration has been reset.`);
		} catch (error) {
			handleError(error);
		}
	};

	const handleRequestApproved = async () => {
		setApproveModalVisible(false);
		try {
			const cloudAccount = selectedCloudAccount;

			handleSubmitAlert('Submitting request...');

			const updatedAccount = cloudAccount.approver ? await cloudAccountsService.approveUserCloudAccount(cloudAccount.id) :
				await cloudAccountsService.approveCloudAccount(cloudAccount.id);

			handleAccountChange(cloudAccount, updatedAccount);

			handleSuccessAlert(`The cloud-only account request '${cloudAccount.createdUserPrincipalName}' has been approved.`);

		} catch (error) {
			handleError(error);
		}
	};

	const handleRequestRejected = async () => {
		setRejectModalVisible(false);
		try {
			const cloudAccount = selectedCloudAccount;

			handleSubmitAlert('Submitting request...');

			const updatedAccount = cloudAccount.approver ? await cloudAccountsService.rejectUserCloudAccount(
				cloudAccount.id, rejectionReason) : await cloudAccountsService.rejectCloudAccount(
					cloudAccount.id, rejectionReason);

			handleAccountChange(cloudAccount, updatedAccount);

			handleSuccessAlert(`The cloud-only account request '${cloudAccount.createdUserPrincipalName}' has been rejected.`);

		} catch (error) {
			handleError(error);
		} finally {
			setRejectionReason('')
		}
	};

	const handleSubmit = async (alertText: string) => {
		setRequestAccountModalVisible(false);
		setDeleteAccountModalVisible(false);
		setAlertVisible(true);
		setAlertType('default');
		setAlertText(alertText);
		setActionsEnabled(false);

		handleSetTimeout();
	};

	const handleSuccess = async (alertText: string) => {

		setResetMfaModalVisible(false);
		setRequestAccountModalVisible(false)
		setDeleteAccountModalVisible(false);
		setAlertVisible(true);
		setAlertType('success');
		setAlertText(alertText);
		setActionsEnabled(true);
		setIsLoading(true);

		handleSetTimeout();

		await updateState();
		setIsLoading(false);
	};

	const handleError = async (error, message = 'Your request could not be completed at this time. Please try again later.') => {
		setResetPasswordModalVisible(false);
		setResetMfaModalVisible(false);
		setRequestAccountModalVisible(false)
		setDeleteAccountModalVisible(false);
		setAlertVisible(true);
		setAlertType('error');
		setAlertText(error.body?.message ?? message);
		setActionsEnabled(true);
		setIsLoading(true);

		handleSetTimeout();
		await updateState();
		setIsLoading(false);

		throw error;
	};

	const handleAlertClose = () => {
		setAlertVisible(false);
		setAlertText('');
		setAlertType('default');
	};

	const handleSetTimeout = () => {
		if (timeoutId !== undefined) {
			window.clearTimeout(timeoutId);
		}

		setTimeoutId(setTimeout(handleAlertClose, 10000));
	};

	return (
		<SectionHomeWrapper title="Cloud-only Accounts Management">
			<TextPassage>
				<p>
					For more information, please follow {' '}
					<TextLink
						target="_blank" external href="https://gotocloud.xom.cloud/cloudwiki/docs/admin/CloudSelfServicePortal.html#cloud-only-accounts"
					>
						this link
					</TextLink>
				</p>
			</TextPassage>
			<br />
			{alertVisible && (
				<Alert
					variant={alertType}
					onClick={handleAlertClose}
					onClose={handleAlertClose}
				>
					{alertText}
				</Alert>
			)}
			<ConfirmModal
				visible={approveModalVisible}
				title={`Approve the cloud-only account request '${selectedCloudAccount?.createdUserPrincipalName}'?`}
				question={`Are you sure you want to approve this request? 
							   A notification will be sent to ${selectedCloudAccount?.workflowInstance.createdBy.displayName} (${selectedCloudAccount?.workflowInstance.createdBy.userPrincipalName.address}) 
							   upon confirmation informing them of this change.`}
				confirmButton={{ label: 'Confirm Approval' }}
				onConfirm={handleRequestApproved}
				onCancel={() => setApproveModalVisible(false)}
			/>
			<ConfirmModal
				visible={rejectModalVisible}
				title={`Reject the cloud-only account request '${selectedCloudAccount?.createdUserPrincipalName}'?`}
				question={`Are you sure you want to reject this request?
							   A notification will be sent to ${selectedCloudAccount?.workflowInstance.createdBy.displayName} (${selectedCloudAccount?.workflowInstance.createdBy.userPrincipalName.address}) 
							   upon confirmation informing them of this change.`}
				confirmButton={{ label: 'Confirm Rejection' }}
				confirmationField={{
					label: 'Please provide a reason for rejecting this request.',
					value: rejectionReason,
					validation: { required: true },
					onChange: (e) => setRejectionReason(e.target.value),
				}}
				onConfirm={handleRequestRejected}
				onCancel={() => (
					setRejectModalVisible(false),
					setRejectionReason('')
				)}
			/>
			<ConfirmModal
				visible={resetPasswordModalVisible}
				title={`Reset the password for '${selectedCloudAccount?.createdUserPrincipalName}'?`}
				question={`An email will be sent to ${selectedCloudAccount?.workflowInstance.owner.displayName}'s primary ExxonMobil email address with a link to complete the process.`}
				onConfirm={handlePasswordResetRequest}
				onCancel={() => setResetPasswordModalVisible(false)}
			/>
			<ConfirmModal
				visible={resetMfaModalVisible}
				title={`Reset MFA configuration for '${selectedCloudAccount?.createdUserPrincipalName}'?`}
				question={`The MFA configuration for the selected account will be reset.`}
				onConfirm={handleMfaResetRequest}
				onCancel={() => setResetMfaModalVisible(false)}
			/>
			<CloudAccountReasonModal
				reasonModalVisible={reasonModalVisible}
				selectedCloudAccount={selectedCloudAccount}
				setReasonModalVisible={setReasonModalVisible}
			/>
			{isLoading ? (
				<ProgressBar indeterminate hideValueLabel label="Loading your accounts..." />
			) : (
				<Table
					header={
						<Toolbar>
							<Toolbar.Item>
								<TextInput
									onChange={handleSearchTextChange}
									value={searchText}
									className="em-u-margin-bottom-none"
									placeholder="Search accounts"
									size="small"
								/>
							</Toolbar.Item>
							<Toolbar.Item>
								<Select
									className="em-u-margin-bottom-none dropdown-filter-color"
									size="small"
									onChange={handleStatusDropdownChange}
									value={searchDropdownStatus}
								>
									<option className='dropdown-filter-color' value="All">All</option>
									<option className='dropdown-filter-color' value="Submitted">Submitted</option>
									<option className='dropdown-filter-color' value="Approved">Approved</option>
									<option className='dropdown-filter-color' value="Rejected">Rejected</option>
									<option className='dropdown-filter-color' value="Deleted">Deleted</option>
									<option className='dropdown-filter-color' value="Error">Error</option>
								</Select>
							</Toolbar.Item>
							<Toolbar.Item right>
								{availableAccountTypes.length === 0 || !authenticatedUser.isInRole(UserRole.CloudAccountUser) ? (
									<Tooltip
										content={
											<Button
												disabled
												variant="primary"
												size="small"
											>
												<PlusFilledIcon size="small" />
												&nbsp; Request Cloud-only Account
											</Button>
										}
									>
										{!authenticatedUser.isInRole(UserRole.CloudAccountUser) ? NoAvailableCloudAccountTypesMessage : AlreadyHasCloudAccountsMessage}
									</Tooltip>
								) : (
									<Button
										variant="primary"
										size="small"
										onClick={() => setRequestAccountModalVisible(true)}
									>
										<PlusFilledIcon size="small" />
										&nbsp; Request Cloud-only Account
									</Button>
								)}
							</Toolbar.Item>
						</Toolbar>
					}
					footer={
						<Paginator
							data={filteredAccounts}
							onPageChange={(accounts: CloudAccount[]) => setAccountsOnPage(accounts)}
						/>
					}
				>
					<Table.Head>
						<Table.Head.Row>
							<Table.Head.Cell>Account Name</Table.Head.Cell>
							<Table.Head.Cell className='em-u-text-align-center'>Status</Table.Head.Cell>
							<Table.Head.Cell className='em-u-text-align-center'>Approver</Table.Head.Cell>
							<Table.Head.Cell className='em-u-text-align-center'>
								{authenticatedUser.isInRole(UserRole.CloudAccountAdmin)
									? 'Requested By'
									: 'Last Modified By'}
							</Table.Head.Cell>
							<Table.Head.Cell className='em-u-text-align-center'>Date Requested</Table.Head.Cell>
						</Table.Head.Row>
					</Table.Head>
					<Table.Body>
						{accountsOnPage.map((cloudAccount) => (
							<CloudAccountsHomeTableRow
								cloudAccount={cloudAccount}
								actionsEnabled={actionsEnabled}
								setResetPasswordModalVisible={setResetPasswordModalVisible}
								setSelectedCloudAccount={setSelectedCloudAccount}
								setResetMfaModalVisible={setResetMfaModalVisible}
								setDeleteAccountModalVisible={setDeleteAccountModalVisible}
								setApproveModalVisible={setApproveModalVisible}
								setRejectModalVisible={setRejectModalVisible}
								setReasonModalVisible={setReasonModalVisible}
							/>
						))}
					</Table.Body>
				</Table>
			)}
			<CloudAccountRequestModal
				visible={requestAccountModalVisible}
				availableAccountTypes={availableAccountTypes}
				onSubmit={handleSubmit}
				onSuccess={handleSuccess}
				onError={handleError}
				onClose={() => setRequestAccountModalVisible(false)}
			/>
			<CloudAccountDeletionModal
				visible={deleteAccountModalVisible}
				cloudAccount={selectedCloudAccount}
				onSubmit={handleSubmitAlert}
				onChange={handleAccountChange}
				onSuccess={handleSuccessAlert}
				onError={handleError}
				onClose={() => setDeleteAccountModalVisible(false)}
			/>
		</SectionHomeWrapper>
	);
}

export default CloudAccountsHome;